import {
  findSynonymsAction,
  getNowWhatSuggestionsAction,
  checkIntroductionAction,
  checkConclusionAction,
  checkTopicSentenceAction,
  findExampleAction,
  findMotJusteAction,
  checkOverviewAction,
} from '@/app/slash-commands/actions';
import type {
  SlashTriggerContext,
  CommandResult,
  CommandContext,
} from '@/types/slash-commands';
import { BodyParagraphType } from '@/types/writing';

export interface SlashCommand {
  id: string;
  name: string;
  description?: string;
  taskTypes: ('task1' | 'task2')[];
  // Determines if the command should be shown based on trigger context
  contextFilter: (context: SlashTriggerContext) => boolean;
  // The server action to execute
  execute: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: CommandContext<any>
  ) => Promise<CommandResult>;
}

// --- Context Filtering Logic --- //

const isBodyParagraph = (
  type: BodyParagraphType | 'introduction' | 'conclusion' | ''
): boolean => type !== 'introduction' && type !== 'conclusion';

// --- Command Definitions --- //

export const COMMANDS: SlashCommand[] = [
  {
    id: 'nowWhat',
    name: 'Now What?',
    description: 'Get suggestions to continue writing',
    taskTypes: ['task1', 'task2'],
    contextFilter: () => true, // Always available
    execute: getNowWhatSuggestionsAction,
  },
  {
    id: 'findSynonym',
    name: 'Find Synonym',
    description: 'Suggest synonyms for the previous word',
    taskTypes: ['task1', 'task2'],
    contextFilter: (ctx) => !!ctx.wordBeforeTrigger, // Only if there's a word before slash
    execute: findSynonymsAction,
  },
  {
    id: 'findExample',
    name: 'Find Example',
    description: 'Suggest providing a specific example',
    taskTypes: ['task2'],
    contextFilter: (ctx) => isBodyParagraph(ctx.currentParagraphType), // Only body
    execute: findExampleAction,
  },
  {
    id: 'checkTopicSentence',
    name: 'Check Topic Sentence',
    description: 'Analyze the first sentence of this paragraph',
    taskTypes: ['task2'],
    contextFilter: (ctx) =>
      isBodyParagraph(ctx.currentParagraphType) &&
      !!ctx.firstSentenceOfParagraph,
    execute: checkTopicSentenceAction,
  },
  {
    id: 'checkIntroduction',
    name: 'Check Introduction',
    description: 'Analyze the introduction paragraph',
    taskTypes: ['task1', 'task2'],
    contextFilter: (ctx) => ctx.currentParagraphType === 'introduction',
    execute: checkIntroductionAction,
  },
  {
    id: 'checkConclusion',
    name: 'Check Conclusion',
    description: 'Analyze the conclusion paragraph',
    taskTypes: ['task2'],
    contextFilter: (ctx) => ctx.currentParagraphType === 'conclusion',
    execute: checkConclusionAction,
  },
  {
    id: 'motJuste',
    name: 'Mot Juste',
    description: 'Suggest appropriate next words.',
    taskTypes: ['task1', 'task2'],
    contextFilter: () => true, // Always available
    execute: findMotJusteAction,
  },
  {
    id: 'checkOverview',
    name: 'Check Overview',
    description: 'Analyze the overview section (Task 1)',
    taskTypes: ['task1'],
    contextFilter: (ctx) => ctx.currentParagraphType === 'conclusion',
    execute: checkOverviewAction,
  },
];
